import { useQuery } from '@tanstack/react-query';
import { supabaseHarvest } from '@/lib/supabase';
import { getTimeframe, type TimeframeKey } from '@/config/timeframes';
import { bucketLabel } from '@/lib/timeBucket';
import { harvestEndpointTypes, type ProviderDefinition, type EntityDefinition } from '@/config/providers';

export interface HarvestSyncPoint {
  bucket: string;
  harvested: number;
  synced: number;
}

export interface HarvestSyncResult {
  points: HarvestSyncPoint[];
  truncated: boolean;
}

/**
 * Per-entity "harvested vs synced" counts over the selected timeframe.
 *  - harvested = harvest.manifest rows for the entity's endpoint_type(s),
 *    bucketed by collected_at.
 *  - synced = rows in the entity's typed table bucketed by updated_at.
 *
 * Both series are rolled up server-side by the harvest.fn_entity_harvest_vs_sync
 * RPC into a gap-filled, fixed-length bucket series for the window. This replaces
 * the prior raw-row pull (capped at 50k rows, bucketed client-side), which
 * silently truncated wide windows and hit the role statement_timeout (57014) on
 * the 100k+ typed tables (e.g. presto_v2.events ≈ 193k rows) because the
 * `ORDER BY updated_at` scan has no supporting index.
 *
 * Caveat: `updated_at` is bumped by ANY write (sync, backfill, enrichment), so
 * the synced line is "rows touched in the DB", a close proxy for sync activity
 * rather than an exact sync-job row count (the per-job counts only go to the
 * RevoStream event bus, not a queryable table). Both series share the timeframe
 * bucketing so they align on the x-axis.
 */
export function useEntityHarvestVsSync(
  provider: ProviderDefinition | null | undefined,
  entity: EntityDefinition | null | undefined,
  timeframeKey: TimeframeKey,
) {
  const tf = getTimeframe(timeframeKey);
  return useQuery({
    queryKey: ['entity-harvest-sync', provider?.key, entity?.table, tf.key],
    enabled: !!provider && !!entity,
    staleTime: 60_000,
    queryFn: async (): Promise<HarvestSyncResult> => {
      const { data, error } = await supabaseHarvest.rpc('fn_entity_harvest_vs_sync', {
        p_provider: provider!.key,
        p_endpoint_types: harvestEndpointTypes(entity!),
        p_schema: provider!.schema,
        p_table: entity!.table,
        p_hours: tf.hours,
        p_bucket_seconds: Math.round(tf.bucketMs / 1000),
      });
      if (error) throw error;
      const points = ((data ?? []) as { bucket_start: string; harvested: number; synced: number }[]).map(
        (r) => ({
          bucket: bucketLabel(new Date(r.bucket_start).getTime(), tf.bucketMs),
          harvested: Number(r.harvested),
          synced: Number(r.synced),
        }),
      );
      return { points, truncated: false };
    },
  });
}
